/**
 * The five checkout acknowledgments, verbatim. Order and wording are
 * load-bearing (legal): Cancellation & Refund Policy, How this works,
 * Chargebacks & Friendly Fraud, Default & Collections, Ticket issuance. The
 * review page renders them inside ONE checkbox's expander
 * (CheckoutAcknowledgments.jsx). Nothing here is a component export.
 *
 * Travel insurance: while the Faye launch flag is on (src/utils/fayeFlag.js),
 * the Cancellation & Refund Policy and the Default & Collections items each
 * carry one extra sentence carving the travel insurance part out. The
 * sentences are CONDITIONAL ("If my trip includes travel insurance, …") and
 * always present, so the text is the same whether or not the customer adds a
 * plan: one assent covers both, and adding or removing insurance never changes
 * what they agreed to. (Until 2026-10-03 the sentences appeared only once a
 * plan was added, which unticked the box and asked the customer to accept
 * again.) With the flag off, all five read exactly as above.
 */
import { FAYE_INSURANCE_ENABLED } from '../../utils/fayeFlag.js';

const LINK_CLASS = 'text-white/55 underline hover:no-underline font-normal cursor-pointer bg-transparent border-0 p-0';

/*
 * TODO(legal): DRAFT — legal must review both carve-outs below, and Faye must
 * approve them (it approves all copy about its plans, Annex A), before the
 * Faye flag is turned on. Open questions for that review:
 *  - Cancellation: the premium is refunded when the trip ends before Faye
 *    issues the policy, but the customer is never told when that is, so the
 *    sentence points at Faye's terms and the cancel screen instead of naming
 *    the condition. Is a reference to "what the cancel screen shows" enough
 *    in a binding acknowledgment, or should it cite Terms §2.17?
 *  - Default: names the condition (an issued policy) in general terms only.
 *    Confirm that is acceptable, and that default notices and collections
 *    exclude unpaid premium on policies that were never issued — a balance
 *    that includes or excludes it would itself tell the customer.
 *  - Chargebacks & Friendly Fraud says "all amounts paid retained" on a
 *    chargeback: decide whether the premium needs the same carve-out.
 *  - Premium in installments: confirm financing an insurance premium this way
 *    is permitted in every state Faye allows Defera to sell in (Faye's guides
 *    don't address it).
 */
/** In the Cancellation & Refund Policy item while the flag is on. */
export const INSURANCE_CANCELLATION_CARVE_OUT = " If my trip includes travel insurance, these rules don't apply to the travel insurance part of my payments, which follows Faye's terms and what the cancel screen shows; any refund of it is made to my card.";

/** In the Default & Collections item while the flag is on. */
export const INSURANCE_DEFAULT_CARVE_OUT = ' If my trip includes travel insurance, its unpaid premium is part of that balance only if my travel insurance policy has been issued.';

/** A Terms / Privacy link that opens the full document without toggling the
 *  checkbox around it. A render helper, not a component, so this file stays
 *  content-only. */
export function renderDocLink(kind, onOpen, label) {
  return (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpen(kind); return false; }}
      onMouseDown={(e) => e.stopPropagation()}
      className={LINK_CLASS}
    >
      {label}
    </button>
  );
}

/**
 * Verbatim acknowledgment copy.
 *
 * @param {Function} onOpen opens the Terms / Privacy document
 * @param {object} [opts]
 * @param {boolean} [opts.enabled] the Faye launch flag (adds the conditional
 *   insurance carve-outs); tests set it to check both states
 */
export function acknowledgmentItems(onOpen, { enabled = FAYE_INSURANCE_ENABLED } = {}) {
  const insured = Boolean(enabled);
  return [
    {
      id: 'cancellation',
      title: 'Cancellation & Refund Policy.',
      body: (
        <>
          I have read and agree to the cancellation and refund rules that apply to my trip: for non-refundable fares, all amounts paid — including deposits, service fees, and any installments already made — are non-refundable if I cancel; for refundable fares, I am eligible for a 75% refund if I cancel up to 24 hours before departure.{insured && INSURANCE_CANCELLATION_CARVE_OUT} I confirm this transaction is a binding payment arrangement with Defera LLC, and I agree to the{' '}
          {renderDocLink('terms', onOpen, 'Terms of Service')}
          {' '}and{' '}
          {renderDocLink('privacy', onOpen, 'Privacy Policy')}.
        </>
      ),
    },
    {
      id: 'how_it_works',
      title: 'How this works.',
      body: (
        <>
          Defera secures my agreed-upon trip price and provides trip-price protection through my installment plan. I agree to repay Defera through the schedule shown above. Airline confirmation numbers and e-tickets are released once I have paid in full and all release conditions are satisfied. Defera may delay ticket issuance or booking while conducting verification, risk review, or other operational processes. Full details are in the{' '}
          {renderDocLink('terms', onOpen, 'Terms of Service')}.
        </>
      ),
    },
    {
      id: 'chargebacks',
      title: 'Chargebacks & Friendly Fraud.',
      body: (
        <>
          I agree not to dispute, chargeback, or reverse any payment properly owed to Defera under my installment plan — including because I haven&apos;t yet received the ticket (by design — Defera provides trip-price protection and installment services, and travel documents are released after full payment) or because the airline&apos;s fare rules don&apos;t permit a refund. Filing a chargeback for amounts properly owed to Defera is <em className="not-italic font-medium text-white/70">friendly fraud</em>, which Defera actively investigates, contests, and prosecutes. I authorize Defera to collect my IP address, geolocation, and billing and contact addresses and to share them with my bank, card network, payment processor, and law-enforcement authorities for fraud defense. If I file such a chargeback, my reservation will be cancelled, all amounts paid retained, and the remaining balance treated as a default. Full legal terms are in the{' '}
          {renderDocLink('terms', onOpen, 'Terms of Service')}{' '}and{' '}
          {renderDocLink('privacy', onOpen, 'Privacy Policy')}.
        </>
      ),
    },
    {
      id: 'default',
      title: 'Default & Collections.',
      body: (
        <>
          I understand that if I default on my installment plan — for example, by failing to make a scheduled payment, by my payment method being declined, expired, closed, or revoked, or by otherwise breaching this agreement — the remaining balance will become immediately due and payable to Defera LLC.{insured && INSURANCE_DEFAULT_CARVE_OUT} I authorize Defera LLC to refer the defaulted balance to one or more third-party collection agencies, and I understand that the collection agency (or Defera, where Defera is a registered data furnisher) may report my default to one or more consumer credit bureaus, which may negatively affect my credit score. Defera may also pursue any other lawful civil remedies to recover amounts owed, including reasonable attorneys&apos; fees and costs where permitted by law. The full legal terms are set out in the{' '}
          {renderDocLink('terms', onOpen, 'Terms of Service')}.
        </>
      ),
    },
    {
      id: 'ticket_issuance',
      title: 'Ticket issuance.',
      body: (
        <>
          I agree to the{' '}
          {renderDocLink('terms', onOpen, 'Terms of Service')}
          {' '}and acknowledge that Defera may, in its sole discretion, delay or withhold ticket issuance while conducting fraud prevention, identity verification, payment verification, risk review, compliance review, pricing validation, supplier confirmation, or other operational reviews. I understand that, even if ticket issuance is delayed, my agreed-upon trip price remains secured and protected from future airfare increases while I remain in good standing on my payment plan.
        </>
      ),
    },
  ];
}
